import useSettingsGlobals from '@/composables/useSettingsGlobals';

// Per-spot persistence of a wind graph's selected model ids. Mirrors the legacy
// _getSavedSelectedModels / _saveSelectedModels: a single localStorage object
// keyed by spotId on the web, and native messaging (android alert / iOS webkit)
// inside the app webview. The localStorage key differs per graph type — basic
// uses 'WindGraphSettings', detailed uses 'DetailedWindGraphSettings' — while
// the native message format is identical for both.
export const useGraphModelStorage = (storageKey = 'WindGraphSettings') => {
  const { $WEB_VIEW, $PLATFORM } = useSettingsGlobals();

  function readStore() {
    try {
      return JSON.parse(window.localStorage.getItem(storageKey)) || {};
    } catch {
      return {};
    }
  }

  // Normalize to numbers so selections compare cleanly against the API model
  // ids (legacy entries / query params may be strings).
  function toIds(values) {
    return values.map(Number).filter((id) => !Number.isNaN(id));
  }

  function read(spotId) {
    if ($WEB_VIEW) {
      const param = new URLSearchParams(window.location.search).get('graph_model_ids');
      return param ? toIds(param.split(',')) : null;
    }

    if (spotId == null || spotId === '') return null;

    const saved = readStore()[spotId];
    return Array.isArray(saved) ? toIds(saved) : null;
  }

  function save(spotId, modelIds) {
    const ids = Array.isArray(modelIds) ? modelIds : [];

    if (!$WEB_VIEW) {
      if (spotId == null || spotId === '') return;

      const store = readStore();
      store[spotId] = ids;
      window.localStorage.setItem(storageKey, JSON.stringify(store));
      return;
    }

    if ($PLATFORM === 'android') {
      alert(['graphModelsChanged', spotId, ids.join(',')].join(','));
      return;
    }

    try {
      window.webkit.messageHandlers.observe.postMessage({ spotId, modelIds: ids, type: 'graph-model-ids' });
    } catch (error) {
      console.warn('Error saving selected wind graph models:', error);
    }
  }

  return { read, save };
};
